import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { isValidHex } from "@/lib/cms/cms-theme.constants";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CMS_COLOR_INPUT_CLASS,
  CMS_COLOR_SWATCH_TRIGGER_CLASS,
} from "./cms-color-input-styles";

function normalizeHex(hex: string) {
  return hex.trim().toUpperCase();
}

type Props = {
  value: string;
  onCommit: (hex: string) => void;
  disabled?: boolean;
  "aria-label"?: string;
  triggerClassName?: string;
};

export function CmsColorPickerField({
  value,
  onCommit,
  disabled = false,
  "aria-label": ariaLabel,
  triggerClassName,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [draftHex, setDraftHex] = useState(value);
  const initialOnOpenRef = useRef(normalizeHex(value));
  const committedOnApplyRef = useRef(false);

  const displayHex = isValidHex(draftHex) ? normalizeHex(draftHex) : value;
  const previewHex = isValidHex(displayHex) ? displayHex : "#000000";

  useEffect(() => {
    if (!open) {
      setDraftHex(value);
    }
  }, [value, open]);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      const normalized = normalizeHex(value);
      initialOnOpenRef.current = normalized;
      setDraftHex(normalized);
      committedOnApplyRef.current = false;
      setOpen(true);
      return;
    }

    if (!committedOnApplyRef.current) {
      const hex = normalizeHex(draftHex);
      if (isValidHex(hex) && hex !== initialOnOpenRef.current) {
        onCommit(hex);
      }
    }
    setOpen(false);
  };

  const handleApply = () => {
    const hex = normalizeHex(draftHex);
    if (!isValidHex(hex)) return;
    committedOnApplyRef.current = true;
    if (hex !== initialOnOpenRef.current) {
      onCommit(hex);
    }
    setOpen(false);
  };

  const handleHexKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleApply();
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel ?? t("cms.palette.pickColor")}
          className={cn(CMS_COLOR_SWATCH_TRIGGER_CLASS, triggerClassName)}
          style={{ backgroundColor: previewHex }}
        />
      </PopoverTrigger>
      <PopoverContent
        align="center"
        sideOffset={6}
        className="w-auto p-3"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col items-center gap-3">
          <input
            type="color"
            value={previewHex}
            disabled={disabled}
            className={CMS_COLOR_INPUT_CLASS}
            aria-label={ariaLabel ?? t("cms.palette.pickColor")}
            onInput={(e) => setDraftHex(e.currentTarget.value)}
          />
          <Input
            value={draftHex}
            disabled={disabled}
            placeholder="#000000"
            className="h-9 w-32 font-mono text-xs"
            onChange={(e) => setDraftHex(e.target.value)}
            onKeyDown={handleHexKeyDown}
          />
          <div className="flex w-full gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => setOpen(false)}
            >
              {t("cms.palette.cancel")}
            </Button>
            <Button
              type="button"
              size="sm"
              className="flex-1"
              disabled={!isValidHex(normalizeHex(draftHex))}
              onClick={handleApply}
            >
              {t("cms.palette.apply")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
